import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import FloatingWhatsApp from '@/components/feature/FloatingWhatsApp';
import ButtonCTA from '@/components/base/ButtonCTA';
import ButtonWhatsApp from '@/components/base/ButtonWhatsApp';
import ButtonPhone from '@/components/base/ButtonPhone';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import PriceTable from '@/components/feature/PriceTable';
import FAQAccordion from '@/components/feature/FAQAccordion';
import CTABlock from '@/components/feature/CTABlock';
import ContactFormBlock from '@/components/feature/ContactFormBlock';
import RevealWrapper from '@/components/base/RevealWrapper';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { REASSURANCE_ITEMS } from '@/mocks/homeData';
import { useDocumentHead } from '@/hooks/useDocumentHead';
import { localBusiness, website, service, faqPage, breadcrumb, article } from '@/lib/seo';

const PRICING_ROWS = [
  { service: 'Traitement ravets logement standard', price: '100 € – 250 €' },
  { service: 'Infestation lourde (blatte germanique installée, 2 passages)', price: '250 € – 410 €' },
  { service: 'Contrat annuel restaurant / commerce alimentaire', price: 'Sur devis, dès 50 €/mois' },
];

const FAQ_ITEMS = [
  {
    question: 'Pourquoi ai-je des ravets alors que ma maison est propre ?',
    answer: 'La propreté limite mais n\'empêche rien : la blatte américaine vient des égouts et des jardins, pas de la saleté. Une maison impeccable avec un siphon sec ou un seuil non étanche reste une porte ouverte.',
  },
  {
    question: 'Les gros ravets qui volent, c\'est traitable aussi ?',
    answer: 'Oui. La blatte américaine se traite par les points d\'entrée (canalisations, regards, seuils) et les zones refuges extérieures. C\'est même le traitement le plus rapide.',
  },
  {
    question: 'Combien de temps pour voir un résultat ?',
    answer: 'Les premiers jours pour le gros de la colonie ; 2 à 3 semaines pour une éradication complète de blatte germanique (le temps que les œufs éclosent et que les jeunes consomment le gel).',
  },
  {
    question: 'Faut-il quitter la maison pendant le traitement ?',
    answer: 'Non. Le gel appât est sans odeur et placé hors de portée des enfants et animaux. Une pulvérisation localisée peut demander de ventiler quelques heures, c\'est tout.',
  },
  {
    question: 'Le traitement tient combien de temps ?',
    answer: 'Plusieurs mois. Avec la prévention (colmatage, siphons), une maison traitée reste tranquille — nos contrats d\'entretien couvrent les cas les plus exposés (rez-de-jardin, proximité de regards).',
  },
  {
    question: 'Ça marche aussi pendant la saison des pluies ?',
    answer: 'Oui, et c\'est même le meilleur moment pour traiter : c\'est là que les ravets cherchent à entrer massivement.',
  },
];

const STEPS = [
  {
    number: '1',
    icon: 'ri-search-eye-line',
    title: 'Inspection',
    description: 'Identification de l\'espèce, localisation des nids (cuisine, salle de bain, gaines techniques, regards extérieurs), évaluation du niveau d\'infestation.',
  },
  {
    number: '2',
    icon: 'ri-medicine-bottle-line',
    title: 'Traitement',
    description: 'Gel appât professionnel dans les zones stratégiques (invisible, sans odeur, sans évacuation des lieux), pulvérisation des zones de passage et points d\'entrée si nécessaire, poudrage des gaines.',
  },
  {
    number: '3',
    icon: 'ri-shield-flash-line',
    title: 'Prévention',
    description: 'Colmatage des passages (siphons, joints, seuils), conseils concrets — les ravets viennent chercher l\'eau avant la nourriture.',
  },
  {
    number: '4',
    icon: 'ri-refresh-line',
    title: 'Suivi',
    description: 'Passage de contrôle pour les infestations installées (blatte germanique), jusqu\'à disparition complète.',
  },
];

export default function RavetsCafards() {
  useDocumentHead({
    title: 'Ravets en Guadeloupe : traitement professionnel anti-cafards',
    description: 'Envahi par les ravets ? Traitement professionnel contre cafards et blattes partout en Guadeloupe. Résultats durables, dès 100 €, intervention sous 48 h.',
    jsonLd: [service({ name: 'Traitement des ravets et cafards', serviceType: 'Désinsectisation', path: '/traitement-ravets-cafards-guadeloupe', description: 'Traitement professionnel des ravets, cafards et blattes en Guadeloupe.' }), faqPage(FAQ_ITEMS), breadcrumb([{ name: 'Accueil', path: '/' }, { name: 'Ravets & cafards', path: '/traitement-ravets-cafards-guadeloupe' }])],
  });

  return (
    <main>
      <Header />
      <Breadcrumb items={[{label:'Accueil',href:'/'},{label:'Ravets & cafards'}]} />

      {/* HERO */}
      <section className="relative bg-primary-500 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/ravets-cafards-1.webp"
            alt="Technicien appliquant un gel anti-ravets dans une cuisine traditionnelle créole en Guadeloupe"
            className="w-full h-full object-cover object-top"
           loading="eager" fetchPriority="high" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(11,58,46,0.94), rgba(11,58,46,0.74) 52%, rgba(11,58,46,0.42))' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(11,58,46,0.55), transparent 38%, transparent 82%, rgba(11,58,46,0.38))' }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-24">
          <div className="max-w-3xl">
            <h1 className="font-heading font-black text-3xl md:text-5xl text-white leading-tight mb-6">
              Ravets en Guadeloupe : le traitement professionnel qui les fait vraiment disparaître
            </h1>
            <p className="text-white/85 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
              Tout le monde en Guadeloupe a déjà vécu ce moment : la lumière s&rsquo;allume dans la cuisine et un gros ravet file sous le réfrigérateur — ou pire, s&rsquo;envole. Les bombes du commerce les font fuir deux jours, puis ils reviennent, toujours plus nombreux. Normal : un traitement de surface ne touche jamais le nid. Nos techniciens traitent la colonie à la source, avec des gels et produits professionnels que les ravets rapportent eux-mêmes au nid.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-4 flex-wrap">
              <ButtonCTA>Devis gratuit</ButtonCTA>
              <ButtonWhatsApp>WhatsApp direct</ButtonWhatsApp>
              <ButtonPhone />
            </div>
          </div>
        </div>
      </section>

      {/* RÉASSURANCE */}
      <ReassuranceBar items={REASSURANCE_ITEMS} />

      {/* RAVET, CAFARD, BLATTE : DE QUI PARLE-T-ON ? */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                  Ravet, cafard, blatte : de qui parle-t-on ?
                </h2>
                <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                  <p>
                    En Guadeloupe, deux espèces font l&rsquo;essentiel des dégâts. <strong>La blatte américaine</strong> — le gros ravet brun-roux de 3 à 4 cm, celui qui vole les soirs de chaleur — vit dans les égouts, les regards et les jardins, et entre dans les maisons par les canalisations et les seuils, surtout en saison des pluies quand les réseaux débordent.
                  </p>
                  <p>
                    <strong>La blatte germanique</strong> — petite, rayée, ultra-prolifique — s&rsquo;installe dans les cuisines : moteur du frigo, dessous d&rsquo;évier, micro-ondes, plinthes. C&rsquo;est elle la plus difficile à éradiquer : une femelle porte des dizaines d&rsquo;œufs, et une cuisine peut héberger des centaines d&rsquo;individus invisibles en journée.
                  </p>
                </div>
              </div>
              <div className="relative">
                <div className="w-full h-72 md:h-80 rounded-xl overflow-hidden">
                  <img
                    src="/images/ravets-cafards-2.webp"
                    alt="Cuisine traditionnelle créole baignée de lumière naturelle à travers les jalousies en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* POURQUOI ILS REVIENNENT TOUJOURS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                Pourquoi ils reviennent toujours (sans traitement professionnel)
              </h2>
              <div className="text-foreground-600 text-base leading-relaxed space-y-4">
                <p>
                  Les ravets sont là depuis 300 millions d&rsquo;années et notre climat est leur paradis : chaleur constante, humidité, abris innombrables. Les insecticides en bombe tuent les individus visibles — 5 % de la colonie — et font fuir les autres plus profond dans les murs.
                </p>
                <p>
                  Pire : beaucoup de populations locales ont développé des résistances aux molécules grand public. Le traitement professionnel fonctionne différemment : gels appâts à effet domino (le ravet contaminé empoisonne le nid), régulateurs de croissance qui stérilisent la colonie, et traitement des points d&rsquo;entrée. Résultat en quelques jours, durable plusieurs mois.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* NOTRE INTERVENTION */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-12">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Notre intervention
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center mb-12">
              <div>
                <p className="text-foreground-600 text-base leading-relaxed">
                  En 4 étapes, de l&rsquo;inspection au suivi, on élimine les ravets à la source. Gel appât professionnel à effet domino, traitement des points d&rsquo;entrée, et surtout : on s&rsquo;assure qu&rsquo;ils ne reviennent pas.
                </p>
              </div>
              <div className="relative">
                <div className="w-full h-64 md:h-72 rounded-xl overflow-hidden">
                  <img
                    src="/images/ravets-cafards-3.webp"
                    alt="Application précise de gel anti-ravets sur les charnières d'un meuble de cuisine en Guadeloupe"
                    className="w-full h-full object-cover"
                   loading="lazy" decoding="async" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {STEPS.map((step) => (
                <div key={step.number} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="w-12 h-12 mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                    <i className={`${step.icon} text-secondary-500 text-xl w-6 h-6 flex items-center justify-center`}></i>
                  </div>
                  <div className="w-7 h-7 mb-3 flex items-center justify-center rounded-full bg-primary-500 text-white font-heading font-bold text-xs">
                    {step.number}
                  </div>
                  <h3 className="font-heading font-bold text-lg text-foreground-950 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed">
                    {step.description}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-8 max-w-3xl mx-auto">
              <div className="bg-secondary-100/50 rounded-xl p-5 border border-secondary-200/50 text-center">
                <p className="text-foreground-700 text-sm leading-relaxed">
                  <strong>Discrétion totale</strong> : véhicule et technicien sans marquage anti-nuisibles criard.
                </p>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* TARIFS */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Nos tarifs de traitement ravets
              </h2>
            </div>
            <div className="max-w-2xl mx-auto">
              <PriceTable rows={PRICING_ROWS} />
              <div className="text-center mt-6">
                <Link
                  to="/tarifs"
                  className="inline-flex items-center gap-2 text-secondary-500 font-semibold hover:text-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Voir tous les tarifs
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* RESTAURANTS, SNACKS, HÔTELS */}
      <section className="bg-white py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl mx-auto text-center">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center rounded-full bg-mango-500/20">
                <i className="ri-alert-line text-mango-500 text-2xl w-8 h-8 flex items-center justify-center"></i>
              </div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-6">
                Restaurants, snacks, hôtels : le ravet est votre pire ennemi
              </h2>
              <p className="text-foreground-600 text-base leading-relaxed max-w-2xl mx-auto">
                Un seul ravet aperçu en salle peut coûter une réputation — et un contrôle sanitaire défavorable. La réglementation HACCP impose un plan de lutte contre les nuisibles documenté. Nos contrats professionnels incluent passages réguliers, registre à jour et intervention d&rsquo;urgence entre deux passages.
              </p>
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* FAQ */}
      <section className="bg-background-100 py-14 md:py-20">
        <RevealWrapper>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-4">
                Questions fréquentes sur les ravets
              </h2>
            </div>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion items={FAQ_ITEMS} />
            </div>
          </div>
        </RevealWrapper>
      </section>

      {/* CTA FINAL */}
      <CTABlock
        title="Marre des ravets ? On les élimine à la source."
      />

      {/* MAILLAGE INTERNE */}
      <section className="bg-background-100 py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-center text-foreground-500 text-sm uppercase tracking-wider mb-4">À lire aussi</p>
            <Link
              to="/desinsectisation-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Traitement désinsectisation
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Tous nos traitements contre les insectes
              </span>
            </Link>
            <Link
              to="/tarifs"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Voir tous nos tarifs
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Prix transparents, devis gratuit
              </span>
            </Link>
            <Link
              to="/blogue/se-debarrasser-des-ravets"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Comment se débarrasser des ravets
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Nos conseils avant l&rsquo;arrivée du technicien
              </span>
            </Link>
            <Link
              to="/deratisation-guadeloupe"
              className="block bg-white rounded-xl p-5 border border-background-200/70 hover:border-secondary-300 transition-colors cursor-pointer"
            >
              <span className="font-heading font-bold text-foreground-950 text-base">
                Rats ET ravets ?
              </span>
              <span className="block text-foreground-500 text-sm mt-1">
                Fréquent — mêmes accès
              </span>
            </Link>
          </div>
        </div>
      </section>

      <ContactFormBlock source="ravets" />

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}